/**
 * The Archive modal body (archive reason + the candidate rejection email).
 * Rendered inside the design-system Modal opened by ActionModals.tsx, and split
 * out of FeedbackModalBodies.tsx when it outgrew the 500-line limit — the same
 * reason MoveBody lives on its own.
 */
import { useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { RadioGroup, RadioItem } from '@finn-auto/design-system/atoms/radio';
import {
  ModalHeader,
  ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from '@finn-auto/design-system/atoms/select';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import { AggFeedbackUnavailable } from './AggFeedbackUnavailable';
import { ArchiveTemplateSwitcher } from './ArchiveTemplateSwitcher';
import { FeedbackCallWarning, GmailNotConnectedWarning } from './ArchiveWarnings';
import { useAggFeedbackParagraph } from './useAggFeedbackParagraph';
import { useGmailConnection } from './GmailConnectionContext';
import type { FeedbackItem } from '../../types/taActionBoard';
import { COLORS } from './format';
import { EmailBodyEditor, EmailRulesInfo } from './EmailBodyEditor';
import { stripFeedbackMarkers } from './aggFeedbackBlock';
import { EmailLibraryLink } from './EmailLibraryLink';
import { AttachmentPicker, type OutboundAttachment } from './AttachmentPicker';
import { ModalActions } from './ModalActions';
import { ScheduleTimePicker } from './ScheduleTimePicker';
import { presetsFor } from './scheduleReplyTime';
import { berlinWallClockToIso } from '../rescheduleForm';

/**
 * ONE merged archive flow: the official Lever archive reason (AI suggestion pre-selected) AND the
 * candidate rejection email in the same modal. The email is drafted server-side and arrives with
 * the board payload; the recruiter reads exactly what the candidate will receive, and confirming
 * counts as approval. Only the Tech-case templates place written feedback in the body — elsewhere
 * the wording is fixed, so there is nothing to edit and we do not pretend otherwise.
 */

/** What the recruiter approved in the modal — text only, never HTML. */
export interface ApprovedEmail {
  subject: string;
  /** Edited HTML from the contentEditable body; sanitised server-side before use. */
  bodyHtml: string;
  language: 'de' | 'en';
  /**
   * One optional file to attach — a job description, prep material, a case study. Null for
   * text only. It rides only an IMMEDIATE send: a queued rejection stores its body in a Firestore
   * document (a ~1 MB ceiling) and this app cannot write to Firebase Storage, so a 3 MB file has
   * nowhere to live there. The modal keeps an attachment and "Send later" mutually exclusive, and
   * the proxy refuses the pair as a backstop.
   */
  attachment: OutboundAttachment | null;
}

export function ArchiveBody({
  item,
  reasons,
  onConfirm,
  onCancel }: {
  item: FeedbackItem;
  reasons: string[];
  /**
   * the approved email (or null to archive without sending), and optionally an ISO time to send it
   * LATER instead of now — the archive commits either way.
   */
  onConfirm: (reason: string, email: ApprovedEmail | null, scheduleAt?: string | null) => void;
  onCancel: () => void;
}) {
  // Pre-select the AI-suggested reason, but only if it really is one of the
  // official Lever archive reasons ("Assessment complete" is already excluded
  // server-side); otherwise fall back to the first official reason.
  const [reason, setReason] = useState(
    item.archiveReason && reasons.includes(item.archiveReason)
      ? item.archiveReason
      : reasons[0] || ''
  );
  const [draft, setDraft] = useState(item.emailDraft);
  const [subject, setSubject] = useState(item.emailDraft?.subject.trim() || '');
  // Default to NOT sending when sending is impossible, so the armed action and
  // the button label can never disagree with what will happen.
  const [skipEmail, setSkipEmail] = useState(false);
  // The email is created as a draft in the recruiter's OWN Gmail, so without a
  // connection there is nowhere for it to go — warn before they confirm, not
  // afterwards in a toast.
  const gmail = useGmailConnection();
  const [switching, setSwitching] = useState(false);
  // The body is one contentEditable field, so its text lives in the DOM rather than in
  // state — mirroring it into state on every keystroke would fight the caret.
  const bodyRef = useRef<HTMLDivElement>(null);
  // innerHTML, not innerText: the recruiter can edit link targets, so the markup is the
  // payload. The server sanitises it to an allowlist before anything is sent. The wrapper that
  // makes an added feedback paragraph removable is internal, so it comes off here.
  const readBody = () => stripFeedbackMarkers(bodyRef.current?.innerHTML ?? '');
  const readBodyText = () => bodyRef.current?.innerText ?? '';
  // Set once the recruiter types, so a language switch can warn before discarding edits.
  const [edited, setEdited] = useState(false);
  /**
   * Whether the body has been emptied — a boolean (not the text) mirrored into state only when it
   * crosses empty↔non-empty, so ordinary keystrokes don't re-render ("don't fight the caret"). It
   * exists so "Send later" cannot be armed against an email with no content (see the confirm guard).
   */
  const [bodyEmpty, setBodyEmpty] = useState(false);
  // "Send later": archive now, queue the rejection email for a chosen Berlin wall-clock time.
  // Defaults to the first preset the moment it is switched on, so there is always a valid time.
  const [sendLater, setSendLater] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  // One optional file on the rejection email (a job description, prep material). It can only ride an
  // immediate send — see ApprovedEmail.attachment — so attaching one switches "Send later" off.
  const [attachment, setAttachment] = useState<OutboundAttachment | null>(null);

  const { feedbackAvailable, feedbackAdded, replacesOffer, toggleFeedback } =
    useAggFeedbackParagraph(draft, bodyRef);

  /**
   * Can the rejection actually leave? Needs the board's Google connection AND an
   * address on the Lever profile. Without both, the send option is disabled
   * rather than offered and silently failing after the archive has committed.
   */
  const canSendRejection = gmail.connected && !!draft?.candidateEmail && !draft?.emailError;

  /**
   * Whether there is an email to send/schedule at all: a draft exists, the recruiter has not chosen
   * "Archive WITHOUT an email", and a send is possible. Gates both the "Send later" control and the
   * confirm-button wording, so the armed action can never disagree with what will happen.
   */
  const emailPossible = Boolean(draft && !skipEmail && canSendRejection);

  const otherLanguage = draft?.language === 'de' ? 'en' : 'de';

  async function switchLanguage() {
    if (!draft || switching) return;
    if (
      edited &&
      !window.confirm('Switching language redrafts the email and discards your edits. Continue?')
    ) {
      return;
    }
    setSwitching(true);
    try {
      const next = await api.previewRejectionEmail(item.opportunityId, otherLanguage);
      setDraft(next);
      setSubject(next.subject.trim());
      setEdited(false);
      setBodyEmpty(false); // the redrafted body is non-empty again
    } catch {
      showToast({ status: 'error', description: 'Could not redraft the email in the other language.' });
    } finally {
      setSwitching(false);
    }
  }
  return (
    <>
      <ModalHeader>
        <ModalTitle>Archive {item.name}?</ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        {/* Defensive: a payload from an older cache may not carry this field, and a
            missing advisory flag must never take the whole board down. */}
        {(item.otherOpenOpportunities ?? []).length > 0 && (
          <div
            className="rounded-lg border p-3 text-xs"
            style={{ backgroundColor: COLORS.redBg, borderColor: COLORS.redText, color: COLORS.redText }}
          >
            <p className="font-semibold">
              {item.name} still has {(item.otherOpenOpportunities ?? []).length} other open{' '}
              {(item.otherOpenOpportunities ?? []).length === 1 ? 'opportunity' : 'opportunities'} at FINN
            </p>
            <ul className="mt-1 space-y-0.5">
              {(item.otherOpenOpportunities ?? []).map((o) => (
                <li key={o.opportunityId}>
                  <a href={o.leverUrl} target="_blank" rel="noreferrer" className="underline">
                    {o.label}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-1">
              Archiving here does not touch those. Consider whether a rejection email is right
              while another application is live.
            </p>
          </div>
        )}
        <p className="text-sm text-gray-600">
          Archive reasons are always one of the official Lever archive reasons — the AI suggestion is
          pre-selected.
        </p>
        <Select value={reason} onValueChange={setReason}>
          {/* w-full: the DS trigger defaults to w-max, which cut long reasons off */}
          <SelectTrigger label="Archive reason" className="w-full">
            <SelectValue placeholder="Choose a reason" />
          </SelectTrigger>
          <SelectContent>
            {reasons.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {draft ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-gray-600">
                Email to {item.name}
                {draft.candidateEmail ? ` (${draft.candidateEmail})` : ''}
              </p>
              <Button
                size="sm"
                variant="ghost"
                disabled={switching || skipEmail || Boolean(draft.emailError)}
                onClick={switchLanguage}
                title={`Redraft this email in ${otherLanguage === 'de' ? 'German' : 'English'}`}
              >
                {switching ? '⏳ redrafting…' : `🌐 ${draft.language.toUpperCase()} → ${otherLanguage.toUpperCase()}`}
              </Button>
            </div>
            {/* One-click template switch — e.g. swap the interview-thanks copy for "Candidate
                unresponsive" on a no-show. Rendered even on an emailError draft, so a recruiter can
                switch AWAY from a missing/auto-picked template to a working one. */}
            <ArchiveTemplateSwitcher
              opportunityId={item.opportunityId}
              language={draft.language}
              disabled={skipEmail || switching}
              hasEdits={edited}
              onSwitched={(next) => {
                setDraft(next);
                setSubject(next.subject.trim());
                setEdited(false);
                setBodyEmpty(false);
              }}
            />
            <Input
              label="Subject"
              value={subject}
              disabled={skipEmail || switching || Boolean(draft.emailError)}
              onChange={(e) => {
                setSubject(e.target.value);
                setEdited(true);
              }}
            />
            {draft.emailError ? (
              /* No copy in the Email Library for this variant. Deliberately no editor and no
                 fallback text: the words a rejected candidate reads belong to TA. The archive
                 itself is still available through the checkbox below. */
              <div className="rounded-lg border border-[#EEC79A] bg-[#FCEEE2] p-3">
                <p className="text-sm font-medium text-[#5C3608]">No email can be drafted</p>
                <p className="mt-1 text-xs text-[#854F0B]">{draft.emailError}</p>
                {/* The message names the template to create; this is the one click that gets there. */}
                <div className="mt-2">
                  <EmailLibraryLink
                    templateKey={draft.templateKey}
                    label="Open this template in the Email Library"
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Above the editor, not in its toolbar: this acts on the whole email (toolbar
                    bold/italic/link act on a selection), and the line beside it says where the
                    words come from. */}
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!feedbackAvailable || skipEmail || switching}
                    onClick={toggleFeedback}
                  >
                    {feedbackAdded ? 'Remove AGG conform feedback' : 'Add AGG conform feedback'}
                  </Button>
                  <span className="text-xs text-gray-500">
                    {draft.usesWrittenFeedback
                      ? 'Already in this email — Tech candidates get the written feedback after a case.'
                      : !draft.feedbackBlockHtml
                        ? <AggFeedbackUnavailable
                            reason={draft.feedbackBlockReason}
                            language={draft.language}
                            topics={draft.feedbackBlockTopics}
                          />
                        : feedbackAdded
                          ? replacesOffer
                            ? 'Included below, in place of the “feel free to reach out” paragraph — the AGG-checked text the Log page keeps. One click takes it out and puts that paragraph back.'
                            : 'Included below — the AGG-checked text the Log page keeps. One click takes it out.'
                          : 'Puts the AGG-checked paragraph from the interview feedback back into the email.'}
                  </span>
                </div>
                <EmailBodyEditor
                  ref={bodyRef}
                  html={draft.html ?? ''}
                  disabled={skipEmail || switching}
                  onChanged={() => {
                    setEdited(true);
                    setBodyEmpty(!readBodyText().trim());
                  }}
                />
                {/* Edits here are for THIS candidate; changing the wording for everyone is an Email
                    Library edit, and this says where (new tab, so the draft survives). */}
                <div className="mt-1 text-right">
                  <EmailLibraryLink templateKey={draft.templateKey} />
                </div>
                {/* One file for THIS candidate (job description, prep material). Immediate send only
                    (see ApprovedEmail.attachment), so picking a file turns "Send later" off below. */}
                <div className="mt-2">
                  <AttachmentPicker
                    attachment={attachment}
                    onChange={(next) => {
                      setAttachment(next);
                      if (next) setSendLater(false);
                    }}
                    disabled={skipEmail || switching}
                    label="Attach a file (optional) — e.g. a job description or prep material"
                  />
                </div>
              </>
            )}
            <FeedbackCallWarning draft={draft} />
            <EmailRulesInfo department={draft.branch.split('_')[0]} stage={draft.branchKey} />
            {skipEmail ? null : <GmailNotConnectedWarning gmail={gmail} />}
            {draft.emailError ? (
              /* #176: no Library copy for this variant, so there is nothing to send and nothing to
                 choose between — the archive is still available. */
              <p className="text-xs text-gray-500">
                Confirming archives the candidate. No email is created, because there is no template
                for it.
              </p>
            ) : (
              <>
                {/* The two outcomes as a visible choice (send vs archive silently); the confirm
                    button says which one is armed. */}
                <RadioGroup
                  value={skipEmail ? 'no-email' : 'send'}
                  onValueChange={(v: string) => setSkipEmail(v === 'no-email')}
                >
                  <RadioItem
                    value="send"
                    disabled={!canSendRejection}
                    label={
                      canSendRejection
                        ? `Send this email to ${draft.candidateEmail} and archive`
                        : gmail.connected
                          ? 'No candidate email on the Lever profile — nothing can be sent'
                          : 'Connect Google above to send from here'
                    }
                  />
                  <RadioItem
                    value="no-email"
                    label="Archive WITHOUT an email — the hiring manager handles the feedback call"
                  />
                </RadioGroup>
              </>
            )}
            {!draft.emailError && (
              <p className="text-xs text-gray-500">
                {skipEmail
                  ? 'The candidate is archived in Lever with the reason above. Nothing is sent.'
                  : 'Sends from your own Gmail after a 10-second countdown you can stop, then archives in Lever with the reason above. It lands in your Sent folder, so a reply comes back to you.'}
              </p>
            )}
          </div>
        ) : (
          /*
            The decision does NOT reach this branch any more: the server drafts an
            email for every pending decision, so arriving here means the draft
            itself could not be built (no usable feedback text, or the model was
            unavailable) — the same fact on a hire card as on a rejection.
          */
          <p className="text-xs text-gray-500">
            No email draft available for this candidate — there was no usable interview feedback
            to base one on. Archiving will not send anything.
          </p>
        )}
      </div>
      {/* Send later: only when there IS an email to send. Archives now, queues the email. */}
      {emailPossible && (
        <div className="rounded-md border border-solid border-gray-200 p-3">
          <Button
            size="sm"
            variant={sendLater ? 'primary' : 'outline'}
            // A queued rejection cannot carry a file (see ApprovedEmail.attachment), so the two are
            // mutually exclusive — remove the attachment to schedule the email for later.
            disabled={!!attachment}
            onClick={() => {
              const on = !sendLater;
              setSendLater(on);
              if (on && !scheduleAt) setScheduleAt(presetsFor()[0]?.value ?? '');
            }}
            data-no-toggle
          >
            🕒 {sendLater ? 'Sending later' : 'Send later'}
          </Button>
          {attachment && (
            <p className="mt-2 text-xs text-gray-500">
              A file can only go with an email sent now — remove the attachment to schedule the email
              for later.
            </p>
          )}
          {sendLater && (
            <div className="mt-3">
              <ScheduleTimePicker value={scheduleAt} onChange={setScheduleAt} />
              {/* An emptied body would make "schedule email" a silent archive-with-no-email — say
                  so and block confirm, rather than quietly dropping the email the label promises. */}
              {bodyEmpty && (
                <p className="mt-2 text-xs text-[#B42318]">
                  There is no email to schedule — add the text above, or switch off “Send later” and
                  choose “Archive WITHOUT an email”.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <ModalActions
        confirmLabel={
          !emailPossible
            ? 'Archive (no email)'
            : sendLater
              ? 'Archive + schedule email'
              : 'Archive + send email'
        }
        confirmDisabled={
          !reason ||
          switching ||
          // "Send later" needs both a valid Berlin time AND an email with content — otherwise the
          // "schedule email" it promises would archive with nothing queued.
          (sendLater && emailPossible && (!berlinWallClockToIso(scheduleAt) || bodyEmpty))
        }
        onConfirm={() => {
          const approvedEmail =
            emailPossible && draft && readBodyText().trim()
              ? // An attachment only ever rides an immediate send; "Send later" is disabled above
                // whenever one is set, so this can carry a file or a scheduleAt, never both.
                { subject, bodyHtml: readBody(), language: draft.language, attachment: sendLater ? null : attachment }
              : null;
          // Guard the silent-archive path: if the recruiter asked to schedule but there is no email
          // to schedule, do nothing rather than archive without the promised mail. The disabled
          // confirm above normally prevents this; this is the last line before the parent closes.
          if (sendLater && emailPossible && !approvedEmail) return;
          onConfirm(reason, approvedEmail, approvedEmail && sendLater ? berlinWallClockToIso(scheduleAt) : null);
        }}
        onCancel={onCancel}
      />
    </>
  );
}
